/**
 * A small popover menu for toolbar buttons (Lit ▾, Show ▾, snap presets,
 * camera speed). Items can be radio-style (one checked) or toggles, and
 * consecutive items with the same `section` sit under its heading (the
 * Cameras list in the projection menu). Esc or a click outside closes it.
 * A menu that would run past the edge of the panel clipping it opens the
 * other way (menuPlacement).
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { ChevronDownIcon } from "./icons";
import { clippingSpan, menuAlignment } from "./menuPlacement";
import type { MenuAlignment } from "./menuPlacement";
import "./dropdown.css";

export interface DropdownItem {
  readonly label: string;
  readonly checked?: boolean;
  readonly onSelect: () => void;
  /** A heading the item sits under, shared with the items next to it. */
  readonly section?: string;
}

/** Runs of consecutive items that share a section (or have none). */
function sectionsOf(items: readonly DropdownItem[]): { section?: string; items: DropdownItem[] }[] {
  const runs: { section?: string; items: DropdownItem[] }[] = [];
  for (const item of items) {
    const last = runs.at(-1);
    if (last && last.section === item.section) last.items.push(item);
    else runs.push({ ...(item.section !== undefined && { section: item.section }), items: [item] });
  }
  return runs;
}

export interface DropdownProps {
  /** Button content. */
  readonly label: ReactNode;
  /** Accessible name for the button. */
  readonly ariaLabel: string;
  readonly items: readonly DropdownItem[];
  /** Radio menus close on select; toggle menus stay open. */
  readonly kind?: "radio" | "toggle";
  readonly showChevron?: boolean;
  readonly className?: string;
}

export function Dropdown({
  label,
  ariaLabel,
  items,
  kind = "radio",
  showChevron = true,
  className,
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const [align, setAlign] = useState<MenuAlignment>("start");
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Before the open menu paints: open the way that keeps it inside the panel clipping it.
  useLayoutEffect(() => {
    const root = rootRef.current;
    const menu = menuRef.current;
    if (!open || !root || !menu) return;
    setAlign(menuAlignment(root.getBoundingClientRect(), menu.offsetWidth, clippingSpan(root)));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const role = kind === "radio" ? "menuitemradio" : "menuitemcheckbox";

  return (
    <div className={`kn-dropdown ${className ?? ""}`} ref={rootRef}>
      <button
        type="button"
        className="kn-dropdown__button"
        aria-label={ariaLabel}
        title={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          setOpen(!open);
        }}
      >
        {label}
        {showChevron && <ChevronDownIcon size={10} className="kn-dropdown__chevron" />}
      </button>
      {open && (
        <div
          ref={menuRef}
          className={`kn-dropdown__menu${align === "end" ? " kn-dropdown__menu--end" : ""}`}
          role="menu"
          aria-label={ariaLabel}
        >
          {sectionsOf(items).map((run, index) => {
            const buttons = run.items.map((item) => (
              <button
                key={item.label}
                type="button"
                role={role}
                aria-checked={item.checked === true}
                className="kn-dropdown__item"
                onClick={() => {
                  item.onSelect();
                  if (kind === "radio") setOpen(false);
                }}
              >
                <span className="kn-dropdown__check" aria-hidden="true">
                  {item.checked ? "✓" : ""}
                </span>
                {item.label}
              </button>
            ));
            if (run.section === undefined) return buttons;
            return (
              <div key={`section-${index}`} role="group" aria-label={run.section}>
                <div className="kn-dropdown__section" aria-hidden="true">
                  {run.section}
                </div>
                {buttons}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
